import React, { useCallback, useContext, useState } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import Modal from 'Pods/Shared/Modal/Modal'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'
import Grid from './Components/Grid/Grid'
import SearchField from './Components/SearchField/SearchField'
import useAcquiredModelOpener from './Hooks/useAcquiredModelOpener'
import useAcquiredModels from './Hooks/useAcquiredModels'

// Fills the first two rows of the widest grid while the first page is in flight.
const INITIAL_SKELETON_COUNT = 9

function ThreeDModelPicker({ onClose: handleClose }) {
  const t = useTranslation()
  const { userLoggedIn } = useContext(ThreeDStudioPageContext)
  const [keywords, setKeywords] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const { isOpening, openModel } = useAcquiredModelOpener()

  const {
    items,
    meta,
    hasNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useAcquiredModels(userLoggedIn, keywords)

  const remainingCount = Math.max(0, (meta?.totalCount ?? 0) - items.length)
  const skeletonCount = Math.min(remainingCount, Number(meta?.perPage) || 10)
  const hasSearched = keywords.length > 0
  const isResolvedEmpty = !isLoading && !isError && items.length === 0
  const showNotFound = isResolvedEmpty && hasSearched
  // The user has acquired nothing at all: no grid to search or select from, so the
  // toolbar and the footer action go with it and the modal offers the two ways out.
  // Gated on !isLoading so the first page renders skeletons rather than flashing empty.
  const showEmptyState = isResolvedEmpty && !hasSearched

  // A selection that survived into a different result set would leave the footer armed
  // for a card the user can no longer see.
  const handleSearch = useCallback((nextKeywords) => {
    setKeywords(nextKeywords)
    setSelectedId(null)
  }, [])

  // An item without a persisted slug has no URL to offer, so it opens at the studio root.
  const handleSubmit = useCallback(async () => {
    const selected = items.find(item => item.id === selectedId)
    const options = selected?.slug
      ? { targetPathname: buildSharedRunPathname({ slug: selected.slug }) }
      : undefined
    const opened = await openModel(selectedId, options)
    if (opened) {
      handleClose()
    }
  }, [openModel, selectedId, items, handleClose])

  const header = t('threeDModelPicker.title')

  const footer = showEmptyState ? null : (
    <Button
      type="primary"
      size="small"
      actionType="button"
      disabled={selectedId === null || isOpening}
      onClick={handleSubmit}
    >
      {t('threeDModelPicker.submit')}
    </Button>
  )

  return (
    <Modal
      header={header}
      ariaLabel={header}
      width="large"
      footer={footer}
      customClass="three-d-model-picker"
      onClose={handleClose}
    >
      {!showEmptyState && (
        <div className="three-d-model-picker__toolbar">
          <span className="three-d-model-picker__heading cgt-heading-100">
            {t('threeDModelPicker.purchasesHeading')}
          </span>
          <SearchField onSearch={handleSearch} />
        </div>
      )}
      {isError && (
        <p className="three-d-model-picker__message cgt-body-md">{t('threeDModelPicker.error')}</p>
      )}
      {showNotFound && (
        <p className="three-d-model-picker__message cgt-body-md">
          {t('threeDModelPicker.notFound', { keywords })}
        </p>
      )}
      {showEmptyState ? (
        <EmptyState onUploadStart={handleClose} />
      ) : (
        <Grid
          items={items}
          selectedId={selectedId}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isLoading || isFetchingNextPage}
          skeletonCount={isLoading ? INITIAL_SKELETON_COUNT : skeletonCount}
          onSelect={setSelectedId}
          onUploadStart={handleClose}
          onLoadMore={handleLoadMore}
        />
      )}
    </Modal>
  )
}

ThreeDModelPicker.propTypes = {
  onClose: func.isRequired,
}

export default ThreeDModelPicker
